import { DialogComponent } from '@theme/dialog';

/**
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLImageElement} image - The full-size image shown in the dialog.
 * @property {HTMLElement} position - The element displaying the current position.
 * @property {HTMLAnchorElement[]} items - The photo links in the grid.
 *
 * @extends DialogComponent
 */
export class GalleryLightbox extends DialogComponent {
  requiredRefs = ['dialog', 'image', 'position', 'items'];

  #index = 0;

  /**
   * @param {number} index - Zero-based index of the photo to show.
   * @param {MouseEvent} event - The click event.
   */
  open = (index, event) => {
    event.preventDefault();

    this.#render(index);
    this.showDialog();

    // `showDialog` defers `showModal()` into a rAF, and `showModal()` focuses the
    // first focusable child — the previous button. Claim focus for the panel on
    // the same frame, right after, so a later arrow key can't flip a button
    // nobody deliberately focused into `:focus-visible` and ring it.
    requestAnimationFrame(() => this.#focusDialog());
  };

  /**
   * @param {MouseEvent} [event] - The click event, when a button was pressed.
   */
  next = (event) => {
    this.#render(this.#index + 1);
    this.#releaseFocus(event);
  };

  /**
   * @param {MouseEvent} [event] - The click event, when a button was pressed.
   */
  previous = (event) => {
    this.#render(this.#index - 1);
    this.#releaseFocus(event);
  };

  /**
   * @param {KeyboardEvent} event - The keydown event.
   */
  handleKeydown = (event) => {
    if (event.key === 'ArrowRight') {
      event.preventDefault();
      this.next();
    } else if (event.key === 'ArrowLeft') {
      event.preventDefault();
      this.previous();
    }
  };

  /**
   * Hands focus back to the dialog after a pointer click, so a following arrow
   * key doesn't flip the clicked button into `:focus-visible` and draw a ring
   * around it. Keyboard activation reports `detail === 0` and keeps its focus,
   * which keyboard users depend on.
   *
   * @param {MouseEvent} [event] - The click event, when there was one.
   */
  #releaseFocus(event) {
    if (!event || event.detail === 0) return;

    this.#focusDialog();
  }

  #focusDialog() {
    this.refs.dialog.focus({ preventScroll: true });
  }

  /**
   * @param {number} index - Index of the photo, wrapped into range.
   */
  #render(index) {
    const { items, image, position } = this.refs;

    if (!items?.length) return;

    const count = items.length;

    this.#index = ((index % count) + count) % count;

    const item = items[this.#index];

    if (!(item instanceof HTMLAnchorElement)) return;

    image.src = item.href;
    image.alt = item.dataset.alt ?? '';
    image.width = Number(item.dataset.width) || 0;
    image.height = Number(item.dataset.height) || 0;

    const template = position.dataset.template ?? '';

    position.textContent = template.replace('[index]', String(this.#index + 1)).replace('[count]', String(count));

    this.#preload(this.#index + 1);
    this.#preload(this.#index - 1);
  }

  /**
   * @param {number} index - Index of the photo, wrapped into range.
   */
  #preload(index) {
    const { items } = this.refs;

    if (!items?.length) return;

    const count = items.length;
    const item = items[((index % count) + count) % count];

    if (!(item instanceof HTMLAnchorElement)) return;

    new Image().src = item.href;
  }
}

if (!customElements.get('gallery-lightbox')) customElements.define('gallery-lightbox', GalleryLightbox);
